import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { MainService } from './main.service';

export interface UserProfile {
  firstName: string;
  name: string;
  email: string;
  photoUrl: string | null;
}

/**
 * Singleton reactive store for the authenticated user's profile.
 * Both ToolbarUserComponent and ToolbarUserDropdownComponent inject this
 * so profile data and photo updates are shared without duplicate API calls.
 */
@Injectable({ providedIn: 'root' })
export class UserProfileService {
  private readonly _profile = new BehaviorSubject<UserProfile | null>(null);
  readonly profile$ = this._profile.asObservable();

  private _loading = false;

  constructor(private readonly mainService: MainService) {}

  /** Fetch profile from backend. No-ops if already loaded or currently loading. */
  async loadProfile(): Promise<void> {
    if (this._profile.value || this._loading) return;
    this._loading = true;
    try {
      const profile = await firstValueFrom(this.mainService.getUserProfile());
      this._profile.next(profile);
    } catch {
      // Silently fail — callers fall back to JWT-decoded data
    } finally {
      this._loading = false;
    }
  }

  /** Update photoUrl after a successful upload without re-fetching the whole profile. */
  setPhoto(photoUrl: string): void {
    const current = this._profile.value;
    if (current) this._profile.next({ ...current, photoUrl });
  }

  /** Upload a new photo, persist to backend, then update the local profile subject. */
  async uploadPhoto(file: File): Promise<void> {
    const result = await firstValueFrom(this.mainService.uploadProfilePhoto(file));
    this.setPhoto(result.photoUrl);
  }

  get snapshot(): UserProfile | null {
    return this._profile.value;
  }
}
